import { useId, useRef, useState } from "react";

import { LodgeApiError } from "../api/client";
import type { PersonWrite } from "../api/hooks";
import {
  PERSON_SOURCES,
  useAddAffiliation,
  useCreatePerson,
  useLodgeUsers,
} from "../api/hooks";
import { SlideOver } from "../components/SlideOver";

import type { ChosenOrganisation } from "./-OrganisationPicker";
import { OrganisationPicker } from "./-OrganisationPicker";

import styles from "./-PersonForm.module.scss";

interface FormFields {
  name: string;
  jobTitle: string;
  email: string;
  phone: string;
  source: string;
  notes: string;
  ownerLodgeUserId: string | null;
}

const emptyFields: FormFields = {
  name: "",
  jobTitle: "",
  email: "",
  phone: "",
  source: "",
  notes: "",
  ownerLodgeUserId: null,
};

// What the API's MaxBytesReader allows for the whole request body, less a
// little for the field names and punctuation that JSON adds around the values.
const MAXIMUM_BODY_BYTES = 7800;

// Enough to catch a typed mistake and no more. The API stores whatever it is
// given, so this is the only check there is - but an address is not a thing a
// regular expression can decide, and refusing a valid oddity would leave an
// operator unable to record somebody who exists.
function looksLikeEmail(email: string): boolean {
  const [local, domain, ...rest] = email.split("@");

  return (
    rest.length === 0 &&
    local !== undefined &&
    local !== "" &&
    domain !== undefined &&
    domain.includes(".") &&
    !domain.startsWith(".") &&
    !domain.endsWith(".") &&
    !/\s/.test(email)
  );
}

// A message worth showing the operator: the API's own http.Error text, which is
// a short single line. A gateway's HTML error page is neither, and it arrives
// through the same channel.
function isReadableDetail(error: LodgeApiError): boolean {
  return (
    error.status >= 400 &&
    error.status < 500 &&
    error.detail !== "" &&
    error.detail.length <= 200 &&
    !error.detail.includes("<")
  );
}

export function PersonForm({ onClose }: { onClose: () => void }) {
  const [fields, setFields] = useState<FormFields>(emptyFields);
  const [organisations, setOrganisations] = useState<ChosenOrganisation[]>([]);
  const [message, setMessage] = useState("");
  const [added, setAdded] = useState("");

  // The person who was written when their affiliations were not. Saving again would
  // write a second person - nothing stops two people with the same name - so
  // the form stops offering that and offers the only recovery that does not:
  // joining the organisations again to the person who already exists.
  const [personNeedingAffiliations, setPersonNeedingAffiliations] = useState<{ id: string; name: string } | null>(null);

  // Document-global ids would collide the moment this panel is composed onto a
  // page that already has a "name" field, and the collision silently steals the
  // label association rather than failing.
  const fieldId = useId();
  const nameInput = useRef<HTMLInputElement>(null);

  const create = useCreatePerson();
  const addAffiliation = useAddAffiliation();
  const {
    data: lodgeUsers,
    isPending: ownersLoading,
    isError: ownersFailed,
  } = useLodgeUsers();

  // Keyed on the field, so setField("name", null) is a compile error rather
  // than an input that silently turns uncontrolled.
  function setField<Field extends keyof FormFields>(
    field: Field,
    value: FormFields[Field],
  ) {
    setFields((previous) => ({ ...previous, [field]: value }));
  }

  function save(again: boolean) {
    setMessage("");
    setAdded("");

    if (personNeedingAffiliations !== null) {
      if (organisations.length === 0) {
        // Every failed organisation was removed by hand, so there is nothing
        // left to retry. Running the loop over an empty list would take the
        // success path and announce links it never wrote.
        //
        // It still has to finish properly. Clearing personNeedingAffiliations and returning left
        // the fields full, the message blank and the button reading "Save"
        // again - a press that did nothing, whose natural answer is to press
        // it again, which writes the person a second time. That is the exact
        // duplicate this whole branch of state exists to prevent.
        finish(personNeedingAffiliations.name, again);
        return;
      }

      void writeAffiliations(personNeedingAffiliations, again);
      return;
    }

    const name = fields.name.trim();
    if (name === "") {
      setMessage("A name is the one thing we cannot record somebody without.");
      return;
    }

    // The API measures the whole body in bytes, and maxLength counts
    // characters: 4000 characters of three-byte UTF-8 is twelve kilobytes and
    // still refused, with a message naming neither field nor limit. Measured
    // here in the same unit the limit is in, and against the body rather than
    // one box, since the other fields count too.
    const body = JSON.stringify({ ...fields, name: fields.name.trim() });
    if (new TextEncoder().encode(body).length > MAXIMUM_BODY_BYTES) {
      setMessage(
        "That is more than we can store. The notes are the usual culprit - shorten them and try again.",
      );
      return;
    }

    const email = fields.email.trim();
    if (email !== "" && !looksLikeEmail(email)) {
      setMessage("That email address does not look right. Check it, or leave it empty.");
      return;
    }

    const write: PersonWrite = {
      name,
      jobTitle: fields.jobTitle.trim(),
      email,
      phone: fields.phone.trim(),
      source: fields.source,
      notes: fields.notes.trim(),
      ownerLodgeUserId: fields.ownerLodgeUserId,
      // The add form does not offer tags. They are the record's, drawn in
      // design 8b's Facts panel and edited there; sending an empty list is
      // what "this form has nothing to say about them" means, and the write
      // shape is the whole person.
      tags: [],
    };

    create.mutate(write, {
      onSuccess: (person) => {
        void writeAffiliations({ id: person.id, name: person.name }, again);
      },
      onError: (error: unknown) => {
        // The server's own words where it gave them: it knows whether the
        // source is one it does not recognise or the name was blank after
        // trimming, and this form does not.
        if (error instanceof LodgeApiError && isReadableDetail(error)) {
          setMessage(error.detail);
          return;
        }

        setMessage("That could not be saved. Try again.");
      },
    });
  }

  // The affiliations, written one at a time after the person exists. They cannot go
  // with the person - an affiliation needs a person id - so this is a second write,
  // and it can fail on its own.
  async function writeAffiliations(person: { id: string; name: string }, again: boolean) {
    // Failures are collected by id, not by name. Two organisations can share
    // one - the picker draws kind and county precisely because they do - so
    // filtering the list by name keeps the one that succeeded as well, the
    // retry re-sends it, the API answers 409 because it is already there, and
    // the panel can never reach an empty failure list again.
    const failed: string[] = [];

    for (const organisation of organisations) {
      try {
        await addAffiliation.mutateAsync({
          personId: person.id,
          organisationId: organisation.id,
          notes: "",
        });
      } catch {
        failed.push(organisation.id);
      }
    }

    if (failed.length > 0) {
      const failedOrganisations = organisations.filter((organisation) =>
        failed.includes(organisation.id),
      );

      // The person exists by now: saying "that could not be saved" sends
      // somebody to type them a second time, and the duplicate is not caught
      // by anything.
      setPersonNeedingAffiliations(person);
      setOrganisations(failedOrganisations);
      setMessage(
        `${person.name} was saved, but ${failedOrganisations
          .map((organisation) => organisation.name)
          .join(" and ")} could not be joined to them. Their details are ` +
          `already stored, so only the organisations will be tried again - ` +
          `edit the person on their own record.`,
      );
      return;
    }

    finish(person.name, again);
  }

  // How every path that has finished with a person ends, so that no branch can
  // invent its own version and leave the form looking untouched.
  //
  // Save and add another: the panel stays, empties, and says who it just
  // saved. Without that line the form looks like it did nothing, and the
  // second person gets typed over the first - or worse, gets written twice
  // because the button appeared not to work.
  function finish(name: string, again: boolean) {
    setPersonNeedingAffiliations(null);

    if (!again) {
      onClose();
      return;
    }

    setFields(emptyFields);
    setOrganisations([]);
    setAdded(`${name} was added.`);
    nameInput.current?.focus();
  }

  const saving = create.isPending || addAffiliation.isPending;

  // After a half-written save the button is retrying the affiliations, not writing a
  // person: saying "Save" would invite the second person the form is refusing
  // to create.
  const saveLabel = personNeedingAffiliations === null ? "Save" : "Try the organisations again";

  return (
    <SlideOver title="Add a person" onClose={onClose} busy={saving}>
      {/* noValidate because "Save and add another" is a button that calls the
          same save directly: with the browser's validation on, a malformed
          email would block one path and sail through the other, and the
          bubble it shows is not the channel this panel reports through. */}
      <form
        className={styles.form}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          save(false);
        }}
      >
        {/* Everything is read-only while the write is in flight. A correction
            typed after Save is accepted by the boxes and then thrown away when
            the panel closes, which looks like the edit was saved. */}
        <fieldset className={styles.fields} disabled={saving}>
          {message !== "" && (
            <div className={styles.alert} role="alert">
              {message}
            </div>
          )}

          {added !== "" && (
            <div className={styles.saved} role="status">
              {added}
            </div>
          )}

          <div className={styles.field}>
            <label className={styles.label} htmlFor={`${fieldId}-name`}>
              Full name
            </label>
            <input
              id={`${fieldId}-name`}
              ref={nameInput}
              className={styles.input}
              value={fields.name}
              onChange={(event) => setField("name", event.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor={`${fieldId}-jobTitle`}>
              Job title
            </label>
            <input
              id={`${fieldId}-jobTitle`}
              className={styles.input}
              value={fields.jobTitle}
              onChange={(event) => setField("jobTitle", event.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor={`${fieldId}-email`}>
              Email
            </label>
            <input
              id={`${fieldId}-email`}
              type="email"
              className={styles.input}
              value={fields.email}
              onChange={(event) => setField("email", event.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor={`${fieldId}-phone`}>
              Phone
            </label>
            <input
              id={`${fieldId}-phone`}
              className={styles.input}
              value={fields.phone}
              onChange={(event) => setField("phone", event.target.value)}
            />
          </div>

          {/* Chips rather than a text box, and the same eight the column allows:
              a filter over free text grows a second spelling that no query
              finds. Picking the chosen one again clears it, because "we do not
              know" is a real answer and a chip list with no way back would make
              a mis-click permanent. */}
          <div className={styles.field}>
            <div className={styles.label} id={`${fieldId}-source`}>
              How we know them
            </div>
            <div
              className={styles.chips}
              role="group"
              aria-labelledby={`${fieldId}-source`}
            >
              {PERSON_SOURCES.map((source) => (
                <button
                  key={source.value}
                  type="button"
                  className={styles.chip}
                  aria-pressed={fields.source === source.value}
                  onClick={() =>
                    setField("source", fields.source === source.value ? "" : source.value)
                  }
                >
                  {source.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.label} id={`${fieldId}-owner`}>
              Owner
            </div>
            {ownersLoading ? (
              // Not an empty picker while the read is in flight. A failed read
              // retries for several seconds before it is a failure, and for that
              // whole window a lone "Unassigned" chip reads as "there is nobody
              // to assign this to" - which is a different thing, and one an
              // operator would act on.
              <div className={styles.chipsEmpty} role="status">
                Loading owners…
              </div>
            ) : ownersFailed ? (
              // Not a silent empty picker: an owner nobody can choose and an
              // owner nobody has chosen look identical, and only one of them is
              // worth retrying.
              <div className={styles.chipsEmpty} role="alert">
                The list of owners could not be loaded. You can save without one.
              </div>
            ) : (
              <div
                className={styles.chips}
                role="group"
                aria-labelledby={`${fieldId}-owner`}
              >
                {/* Unassigned is a real answer rather than the absence of one:
                    most records belong to nobody until somebody picks them up. */}
                <button
                  type="button"
                  className={styles.chip}
                  aria-pressed={fields.ownerLodgeUserId === null}
                  onClick={() => setField("ownerLodgeUserId", null)}
                >
                  Unassigned
                </button>
                {(lodgeUsers ?? []).map((lodgeUser) => (
                  <button
                    key={lodgeUser.id}
                    type="button"
                    className={styles.chip}
                    aria-pressed={fields.ownerLodgeUserId === lodgeUser.id}
                    onClick={() => setField("ownerLodgeUserId", lodgeUser.id)}
                  >
                    {lodgeUser.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <OrganisationPicker
            chosen={organisations}
            disabled={saving}
            onChoose={(organisation) =>
              setOrganisations((previous) => [...previous, organisation])
            }
            onRemove={(organisationId) =>
              setOrganisations((previous) =>
                previous.filter((organisation) => organisation.id !== organisationId),
              )
            }
          />

          <div className={styles.field}>
            <label className={styles.label} htmlFor={`${fieldId}-notes`}>
              Notes
            </label>
            <textarea
              id={`${fieldId}-notes`}
              className={styles.textarea}
              // A first line of defence only - it counts characters where the
              // API counts bytes, so save() measures the real thing.
              maxLength={4000}
              value={fields.notes}
              onChange={(event) => setField("notes", event.target.value)}
            />
          </div>

          {/* Saving with no organisation is a first-class path, not a failure:
              somebody who arrived from a personal address sits in the Unlinked
              view until a colleague joins them to the body they came from.
              Said only while it is true - with an organisation chosen it is a
              promise about the wrong outcome. */}
          {organisations.length === 0 && (
            <div className={styles.hint}>
              They will be saved unlinked, and sit in the Unlinked view until
              somebody joins them to an organisation.
            </div>
          )}

        </fieldset>

        <div className={styles.footer}>
          <button type="submit" className={styles.save} disabled={saving}>
            {saving ? "Saving…" : saveLabel}
          </button>
          <button
            type="button"
            className={styles.secondary}
            disabled={saving}
            onClick={() => save(true)}
          >
            Save and add another
          </button>
          <button
            type="button"
            className={styles.cancel}
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </form>
    </SlideOver>
  );
}
